import { useUserOptional } from "@/components/auth-provider";
import { usePathname } from "@/hooks/use-pathname-compat";
import { isPublicRoute } from "@/lib/public-routes";
import { PermissionWrapper } from "./permission-wrapper";

// Define route permissions based on permission levels
// Level 0: Basic access (messages, basic views)
// Level 1: Messages and Stocks access
// Level 2: Most features access
// Level 3: Full access (admin features)
const routePermissions: { [key: string]: number } = {
  // Profile and parameters (Level 0 - always accessible)
  "/profile": 0,
  "/profile/[id]": 0,

  // Core messaging and basic features (Level 1)
  "/chat": 1,
  "/chat/recap": 1,
  "/chat/unanswered": 1,
  "/chat_test": 1,
  "/chat_test/recap": 1,
  "/chat_test/unanswered": 1,

  // Stock management and operations (Level 1)
  "/stocks": 1,
  "/products": 1,
  "/products/[id]": 1,
  "/products/[id]/components/stat": 1,
  "/operations": 1,
  "/steering": 1,
  "/steering/[id]": 1,
  "/orders": 1,
  "/orders/[id]": 1,
  "/audio_orders": 1,
  "/flows": 1,
  "/saved-offers": 1,
  "/saved-offers/[id]": 1,
  "/contacts": 1,
  "/whatsapp": 1,
  "/whatsapp/recap": 1,
  "/ressere": 1,

  // Advanced features and management (Level 2)
  "/partners": 1,
  "/partners/[id]": 1,
  "/emails": 1,
  "/daily_recap/[date]": 1,

  // Administrative and financial features (Level 3)
  "/accounting": 1,
  "/billing": 1,
  "/parameters": 0,
  "/company": 1,
};

export function PermissionCheck({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const user = useUserOptional();

  // Public pre-auth routes (and the landing page) skip permission checks. The
  // route list is shared with PageContent via isPublicRoute so the two gates
  // cannot drift.
  if (pathname === "/" || isPublicRoute(pathname)) {
    return <>{children}</>;
  }

  // If no user is logged in, redirect to login page
  if (!user) {
    window.location.href = `/login?next=${encodeURIComponent(pathname)}`;
    return null;
  }

  // Get user's permission level
  const userPermissionLevel = user.feature_permissions || 0;

  // If user has level 3 permission, grant access to everything
  if (userPermissionLevel === 3) {
    return <>{children}</>;
  }

  // Check if the exact path exists in routePermissions
  if (pathname in routePermissions) {
    const requiredLevel = routePermissions[pathname];
    if (userPermissionLevel >= requiredLevel) {
      return <>{children}</>;
    }
    return (
      <PermissionWrapper
        requiredPermissions={[`Permission level ${requiredLevel} required`]}
        userPermissions={[`Current level: ${userPermissionLevel}`]}
      >
        {children}
      </PermissionWrapper>
    );
  }

  // Check for dynamic routes by replacing dynamic segments with placeholders
  const dynamicPathChecks = [
    // Replace IDs with placeholders for permission checking
    pathname.replace(/\/[^\/]+$/, "/[id]"),
    pathname.replace(/\/\d{4}-\d{2}-\d{2}$/, "/[date]"),
    // Handle nested dynamic routes
    pathname.replace(/\/[^\/]+\/components\/stat$/, "/[id]/components/stat"),
  ];

  for (const dynamicPath of dynamicPathChecks) {
    if (dynamicPath in routePermissions) {
      const requiredLevel = routePermissions[dynamicPath];
      if (userPermissionLevel >= requiredLevel) {
        return <>{children}</>;
      }
      return (
        <PermissionWrapper
          requiredPermissions={[`Permission level ${requiredLevel} required`]}
          userPermissions={[`Current level: ${userPermissionLevel}`]}
        >
          {children}
        </PermissionWrapper>
      );
    }
  }

  // Check for parent paths if exact path is not found
  // For example, if /stocks/123/details is accessed, check permissions for /stocks
  const parentPath = Object.keys(routePermissions).find(
    (route) =>
      pathname.startsWith(route) && route !== pathname && !route.includes("["),
  );

  if (parentPath) {
    const requiredLevel = routePermissions[parentPath];
    if (userPermissionLevel >= requiredLevel) {
      return <>{children}</>;
    }
    return (
      <PermissionWrapper
        requiredPermissions={[`Permission level ${requiredLevel} required`]}
        userPermissions={[`Current level: ${userPermissionLevel}`]}
      >
        {children}
      </PermissionWrapper>
    );
  }

  // If no matching route permissions found, default to level 1 requirement for security
  if (userPermissionLevel >= 1) {
    return <>{children}</>;
  }

  return (
    <PermissionWrapper
      requiredPermissions={[`Permission level 1 required`]}
      userPermissions={[`Current level: ${userPermissionLevel}`]}
    >
      {children}
    </PermissionWrapper>
  );
}
